import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { toLocalDateString, formatDateLongDisplay } from '@/lib/dateUtils';
import { formatDisplayText } from '@/lib/displayText';
import { computeCateringSubtotal, computeCateringFinalPricing } from '@shared/cateringPricing';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, Clock, Users, IndianRupee, ChefHat, Utensils, Coffee, Cake, Plus, Minus, ShoppingCart, Calendar, Settings, Save, Loader2, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

interface CateringPackage {
  id: number;
  name: string;
  description: string;
  pricePerPerson: string;
  guestRangeMin: number;
  guestRangeMax: number;
  minJuices: number;
  minStarters: number;
  minBreads: number;
  minGravies: number;
  minRiceDishes: number;
  minDesserts: number;
  includesSaladCounter: boolean;
  includesPremiumSetting: boolean;
  includesWaterAndStaff: boolean;
}

interface MenuItem {
  id: number;
  name: string;
  description: string;
  price: string;
  isVegetarian: boolean;
  isSpicy?: boolean;
  preparationTime?: number;
  servingSize?: string;
}

interface Category {
  id: number;
  name: string;
  displayName: string;
  description: string;
  sortOrder: number;
  menuItems: MenuItem[];
}

interface OrderSelection {
  categoryId: number;
  categoryName: string;
  menuItemId: number;
  menuItemName: string;
  price: string;
}

// Loads Razorpay checkout.js once and reports readiness (mirrors van-rental.tsx).
// Only when `enabled`: booting it starts Razorpay's own frame, which requests
// checkout-static-next.razorpay.com/build/undefined — a failed load and a console error on every visit
// to a page most people only browse (audit R4-12, same as Add-ons b7a97fe3b).
function useRazorpayScript(enabled: boolean) {
  const [loaded, setLoaded] = useState(
    typeof window !== 'undefined' && !!(window as any).Razorpay
  );
  useEffect(() => {
    if (loaded || !enabled) return;
    if ((window as any).Razorpay) { setLoaded(true); return; }
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
    );
    if (existing) {
      existing.addEventListener('load', () => setLoaded(true));
      if ((window as any).Razorpay) setLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setLoaded(true);
    document.body.appendChild(script);
  }, [loaded, enabled]);
  return loaded;
}

export default function CateringOrder() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // State management
  const [selectedPackage, setSelectedPackage] = useState<CateringPackage | null>(null);
  const [guestCount, setGuestCount] = useState<number>(1);
  const [selectedWedding, setSelectedWedding] = useState<number | null>(null);
  const [selections, setSelections] = useState<OrderSelection[]>([]);
  const [specialRequests, setSpecialRequests] = useState<string>('');
  const [deliveryDate, setDeliveryDate] = useState<string>('');
  const [deliveryTime, setDeliveryTime] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<'package' | 'menu' | 'details' | 'review'>('package');
  // Loaded from Review on, so the Pay button on the next step is ready when they get there.
  const razorpayReady = useRazorpayScript(currentStep === 'review' || (currentStep as string) === 'payment');
  const [activeTab, setActiveTab] = useState<string>('');
  
  // Auto-save state
  const [draftOrderId, setDraftOrderId] = useState<number | null>(null);
  const [completedCategories, setCompletedCategories] = useState<number>(0);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isAutoSaving, setIsAutoSaving] = useState<boolean>(false);

  // Fetch catering packages
  const { data: packages = [], isLoading: packagesLoading } = useQuery({
    queryKey: ['/api/catering/packages'],
  });

  // Fetch catering categories with menu items
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['/api/catering/categories'],
    enabled: !!selectedPackage,
  });



  // Initialize active tab when categories load
  useEffect(() => {
    if (categories.length > 0 && !activeTab) {
      setActiveTab(categories[0].name);
    }
  }, [categories, activeTab]);

  // Auto-select mandatory categories (Salad Counter and Settings & Staff)
  useEffect(() => {
    if (categories.length > 0) {
      const mandatoryCategories = ['salad_counter', 'settings_staff'];
      const newSelections: OrderSelection[] = [];
      
      mandatoryCategories.forEach(categoryName => {
        const category = categories.find(cat => cat.name === categoryName);
        if (category && category.menuItems.length > 0) {
          const existingSelections = selections.filter(s => s.categoryName === categoryName);
          
          if (categoryName === 'settings_staff') {
            // For Settings & Staff, auto-select ALL 5 items (they're all mandatory)
            if (existingSelections.length === 0) {
              category.menuItems.forEach(item => {
                const mandatorySelection: OrderSelection = {
                  categoryId: category.id,
                  categoryName: categoryName,
                  menuItemId: item.id,
                  menuItemName: item.name,
                  price: item.price
                };
                newSelections.push(mandatorySelection);
              });
            }
          } else {
            // For other mandatory categories (salad_counter), auto-select first item only
            if (existingSelections.length === 0) {
              const item = category.menuItems[0];
              const mandatorySelection: OrderSelection = {
                categoryId: category.id,
                categoryName: categoryName,
                menuItemId: item.id,
                menuItemName: item.name,
                price: item.price
              };
              newSelections.push(mandatorySelection);
            }
          }
        }
      });
      
      if (newSelections.length > 0) {
        setSelections(prev => [...prev, ...newSelections]);
      }
    }
  }, [categories]); // Removed 'selections' dependency to prevent infinite loop

  // Fetch user's weddings
  const { data: weddings = [] } = useQuery({
    queryKey: ['/api/weddings'],
  });

  // Fetch existing draft order
  const { data: draftOrder } = useQuery({
    queryKey: ['/api/catering/orders/draft', selectedWedding],
    enabled: !!selectedWedding,
  });

  // Auto-save draft order mutation
  const autoSaveDraftMutation = useMutation({
    mutationFn: async (draftData: any) => {
      const response = await apiRequest('/api/catering/orders/draft', { method: 'POST', data: draftData });
      return response.json();
    },
    onSuccess: (data) => {
      setDraftOrderId(data.orderId);
      setLastSavedAt(new Date());
      setIsAutoSaving(false);
      console.log('Draft order auto-saved successfully');
    },
    onError: (error) => {
      setIsAutoSaving(false);
      console.error('Error auto-saving draft order:', error);
    }
  });

  // Create final order directly (bypassing draft system)
  const createFinalOrderMutation = useMutation({
    mutationFn: async (orderData: any) => {
      console.log('Frontend sending order data:', orderData);
      try {
        const response = await apiRequest('/api/catering/orders/create-final', {
          method: 'POST',
          data: orderData
        });
        const result = await response.json();
        console.log('Backend response:', result);
        return result;
      } catch (error: any) {
        console.error('Frontend API error:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('Order created successfully:', data);
      // Continue with payment processing
      handleRazorpayPayment(data.order);
    },
    onError: (error: any) => {
      console.error('Order creation error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      toast({
        title: "Failed to create order",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    }
  });

  // Finalize draft order mutation (convert draft to final order)
  const finalizeDraftMutation = useMutation({
    mutationFn: async (finalizeData: any) => {
      if (draftOrderId) {
        // Convert existing draft to final order
        const response = await apiRequest(`/api/catering/orders/finalize/${draftOrderId}`, { method: 'POST', data: finalizeData });
        return response.json();
      } else {
        // Create new final order directly
        const response = await apiRequest('/api/catering/orders', { method: 'POST', data: finalizeData });
        return response.json();
      }
    },
    onSuccess: (data) => {
      console.log('Order finalized successfully:', data);
      // Continue with payment processing
      handleRazorpayPayment(data.order);
    },
    onError: (error) => {
      console.error('Error finalizing order:', error);
      toast({
        title: "Error",
        description: "Failed to process order. Please try again.",
        variant: "destructive",
      });
    }
  });

  // Load existing draft order data
  useEffect(() => {
    if (draftOrder && !isAutoSaving) {
      // Restore draft order state
      setGuestCount(draftOrder.guestCount);
      setDraftOrderId(draftOrder.id);
      setCompletedCategories(draftOrder.completedCategories || 0);
      setLastSavedAt(new Date(draftOrder.lastSavedAt));
      
      // Restore selections from draft
      if (draftOrder.selections && draftOrder.selections.length > 0) {
        const restoredSelections = draftOrder.selections.map((sel: any) => ({
          categoryId: sel.categoryId,
          categoryName: sel.categoryName,
          menuItemId: sel.menuItemId,
          menuItemName: sel.menuItemName,
          price: sel.price
        }));
        setSelections(restoredSelections);
      }
    }
  }, [draftOrder, isAutoSaving]);

  // Auto-save functionality - DISABLED due to payment system conflicts
  const triggerAutoSave = () => {
    // AUTO-SAVE DISABLED - was causing payment processing errors
    return;
    
    // if (!selectedWedding || !selectedPackage || isAutoSaving) return;

    // // Count completed categories
    // const categoryOrder = ['fresh_juices', 'starters', 'salad_counter', 'breads', 'gravies', 'rice_dishes', 'curry_for_rice', 'non_veg_items', 'veg_counter', 'desserts', 'live_counters', 'settings_staff'];
    // let completed = 0;
    
    // categoryOrder.forEach(categoryName => {
    //   const categorySelections = selections.filter(s => s.categoryName === categoryName);
    //   if (categorySelections.length > 0) {
    //     completed++;
    //   }
    // });

    // // Auto-save after every 3 categories (3, 6, 9, 12) - DISABLED
    // if (completed > 0 && completed % 3 === 0 && completed !== completedCategories) {
    //   setIsAutoSaving(true);
    //   setCompletedCategories(completed);
      
    //   const draftData = {
    //     packageId: selectedPackage.id,
    //     weddingId: selectedWedding,
    //     guestCount,
    //     selections: selections.map(sel => ({
    //       categoryId: sel.categoryId,
    //       menuItemId: sel.menuItemId
    //     })),
    //     completedCategories: completed,
    //     isDraft: true
    //   };

    //   autoSaveDraftMutation.mutate(draftData);
    // }
  };

  // Trigger auto-save when selections change - DISABLED
  // useEffect(() => {
  //   const timeoutId = setTimeout(() => {
  //     triggerAutoSave();
  //   }, 1000); // Debounce to avoid too frequent calls

  //   return () => clearTimeout(timeoutId);
  // }, [selections, selectedPackage, selectedWedding, guestCount]);

  // Auto-detect event and set delivery date
  useEffect(() => {
    if (weddings.length === 1) {
      setSelectedWedding(weddings[0].id);
      // Set delivery date to event date if available
      if (weddings[0].weddingDate) {
        setDeliveryDate(toLocalDateString(weddings[0].weddingDate));
        
        // Auto-set delivery time based on event time
        const eventTime = weddings[0].weddingTime || weddings[0].timeSlot || '';
        if (eventTime.toLowerCase().includes('evening') || eventTime.includes('PM') || eventTime.includes('pm')) {
          setDeliveryTime('evening');
        } else {
          setDeliveryTime('day');
        }
      }
    }
  }, [weddings]);

  // Calculate final payment amounts (discounts, GST, setup fee, 20% advance).
  // Uses the SHARED pricing module so the displayed amount == the server-charged amount.
  const calculateFinalPricing = () => computeCateringFinalPricing(calculateTotalPrice(), guestCount);

  // Handle Razorpay payment for finalized order
  const handleRazorpayPayment = (finalizedOrder: any) => {
    // Create Razorpay order for advance amount (server computes the amount)
    createOrderMutation.mutate({
      orderId: finalizedOrder.id,
      packageId: finalizedOrder.packageId,
      guestCount: finalizedOrder.guestCount,
      specialRequests: finalizedOrder.specialRequests,
      deliveryDate: finalizedOrder.deliveryDate,
      deliveryTime: finalizedOrder.deliveryTime
    });
  };

  // Razorpay payment mutation
  const createOrderMutation = useMutation({
    mutationFn: async (orderData: any) => {
      // Create Razorpay order — server recomputes the advance from the stored
      // order (the client-sent amount is NOT trusted).
      const paymentResponse = await apiRequest('/api/catering/create-payment', {
        method: 'POST',
        data: {
          orderData: {
            ...orderData,
            paymentType: 'advance',
          },
        },
      });
      return paymentResponse.json();
    },
    onSuccess: (data) => {
      // Initialize Razorpay payment
      const options = {
        key: data.keyId || import.meta.env.VITE_RAZORPAY_KEY_ID,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpayOrderId,
        name: 'LA Mirage Catering',
        description: `Catering Order - ${selectedPackage?.name}`,
        handler: async function (response: any) {
          try {
            // Verify payment and confirm the order
            await apiRequest('/api/catering/verify-payment', {
              method: 'POST',
              data: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                orderData: data.orderData,
              },
            });
            
            toast({
              title: "Payment Successful",
              description: "Your catering order has been confirmed and paid.",
            });
            
            // Reset form
            setCurrentStep('package');
            setSelections([]);
            setSelectedPackage(null);
            setSpecialRequests('');
            setDeliveryDate('');
            setDeliveryTime('');
            
            queryClient.invalidateQueries({ queryKey: ['/api/catering/orders/client'] });
            
          } catch (error) {
            toast({
              title: "Payment Verification Failed",
              description: "Payment was successful but order verification failed. Please contact support.",
              variant: "destructive",
            });
          }
        },
        prefill: {
          name: '',
          email: '',
          contact: ''
        },
        theme: {
          color: '#B8860B'
        }
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    },
    onError: (error: any) => {
      const errorMessage = error.message || "Failed to initialize payment";
      toast({
        title: "Payment Setup Failed",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Calculate minimum requirements progress
  const calculateProgress = () => {
    if (!selectedPackage) return {};
    
    const categorySelections = {};
    selections.forEach(selection => {
      if (!categorySelections[selection.categoryName]) {
        categorySelections[selection.categoryName] = 0;
      }
      categorySelections[selection.categoryName]++;
    });

    return {
      juices: {
        current: categorySelections['juices'] || 0,
        required: 4, // Fresh juice category requires exactly 4 selections (mocktails optional)
        progress: Math.min(100, ((categorySelections['juices'] || 0) / 4) * 100)
      },
      starters: {
        current: categorySelections['starters'] || 0,
        required: 2, // Starters category: minimum 1, maximum 2 selections for ₹40 base price
        progress: Math.min(100, ((categorySelections['starters'] || 0) / 1) * 100) // Progress based on minimum 1
      },
      salad_counter: {
        current: categorySelections['salad_counter'] || 0,
        required: 1, // Salad Counter: mandatory single selection at ₹25
        progress: Math.min(100, ((categorySelections['salad_counter'] || 0) / 1) * 100)
      },
      breads: {
        current: categorySelections['breads'] || 0,
        required: 2, // Breads category: mandatory 2 selections for ₹20 base price
        progress: Math.min(100, ((categorySelections['breads'] || 0) / 2) * 100)
      },
      gravies: {
        current: categorySelections['gravies'] || 0,
        required: selectedPackage.minGravies,
        progress: Math.min(100, ((categorySelections['gravies'] || 0) / selectedPackage.minGravies) * 100)
      },
      rice_dishes: {
        current: categorySelections['rice_dishes'] || 0,
        required: selectedPackage.minRiceDishes,
        progress: Math.min(100, ((categorySelections['rice_dishes'] || 0) / selectedPackage.minRiceDishes) * 100)
      },
      curry_for_rice: {
        current: categorySelections['curry_for_rice'] || 0,
        required: 0, // Curry For Rice: Completely optional category with no minimum requirements
        progress: 100 // Always complete since it's optional
      },
      non_veg_items: {
        current: categorySelections['non_veg_items'] || 0,
        required: 0, // Non Veg Items: Optional premium category with individual pricing
        progress: 100 // Always complete since it's optional
      },
      veg_counter: {
        current: categorySelections['veg_counter'] || 0,
        required: 1, // Veg Counter: mandatory single selection at ₹30
        progress: Math.min(100, ((categorySelections['veg_counter'] || 0) / 1) * 100)
      },
      desserts: {
        current: categorySelections['desserts'] || 0,
        required: 1, // Desserts: mandatory minimum 1 selection with ₹25 base price + item pricing
        progress: Math.min(100, ((categorySelections['desserts'] || 0) / 1) * 100)
      },
      live_counters: {
        current: categorySelections['live_counters'] || 0,
        required: 0, // Live Counters: Optional category with individual pricing
        progress: 100 // Always complete since it's optional
      },
      settings_staff: {
        current: categorySelections['settings_staff'] || 0,
        required: 5, // Settings & Staff: mandatory auto-selected category with all 5 items at ₹30
        progress: Math.min(100, ((categorySelections['settings_staff'] || 0) / 5) * 100)
      }
    };
  };

  // Check if minimum requirements are met for current step
  const areMinimumRequirementsMet = () => {
    if (!selectedPackage) return false;
    
    // Validate ALL categories are completed before proceeding to order details
    const categorySelections = {};
    selections.forEach(selection => {
      if (!categorySelections[selection.categoryName]) {
        categorySelections[selection.categoryName] = 0;
      }
      categorySelections[selection.categoryName]++;
    });

    // Check all category requirements
    const juiceCount = categorySelections['juices'] || 0;
    const startersCount = categorySelections['starters'] || 0;
    const saladCounterCount = categorySelections['salad_counter'] || 0;
    const breadsCount = categorySelections['breads'] || 0;
    const graviesCount = categorySelections['gravies'] || 0;
    const riceDishesCount = categorySelections['rice_dishes'] || 0;
    const vegCounterCount = categorySelections['veg_counter'] || 0;
    const dessertsCount = categorySelections['desserts'] || 0;
    const settingsStaffCount = categorySelections['settings_staff'] || 0;

    // Calculate base starters count (₹0 items only) for validation
    const baseStartersCount = selections.filter(s => {
      if (s.categoryName !== 'starters') return false;
      const starterItem = categories?.find(cat => cat.name === 'starters')?.menuItems.find(item => item.id === s.menuItemId);
      return starterItem && parseFloat(starterItem.price) === 0;
    }).length;

    const allRequirementsMet = (
      juiceCount === 4 && // Exactly 4 juice selections
      baseStartersCount >= 1 && baseStartersCount <= 2 && // Base starters: minimum 1, maximum 2 for ₹40 (optional sections unlimited)
      saladCounterCount === 1 && // Salad Counter: mandatory 1 selection at ₹25
      breadsCount === 2 && // Breads: mandatory 2 selections for ₹20 base
      graviesCount >= selectedPackage.minGravies &&
      riceDishesCount >= selectedPackage.minRiceDishes &&
      vegCounterCount === 1 && // Veg Counter: mandatory 1 selection at ₹30
      dessertsCount >= 1 && // Desserts: mandatory minimum 1 selection with ₹25 base price + item pricing
      settingsStaffCount === 5 // Settings & Staff: mandatory all 5 items selected at ₹30
    );

    console.log('Menu Requirements Check:', {
      juices: `${juiceCount}/4`,
      base_starters: `${baseStartersCount}/2`, // Base starters: minimum 1, maximum 2 for ₹40
      total_starters: `${startersCount}`, // Total starters (base + optional unlimited)
      salad_counter: `${saladCounterCount}/1`, // Salad Counter: mandatory 1
      breads: `${breadsCount}/2`, // Breads: mandatory 2 selections for ₹20
      gravies: `${graviesCount}/${selectedPackage.minGravies}`,
      rice_dishes: `${riceDishesCount}/${selectedPackage.minRiceDishes}`,
      veg_counter: `${vegCounterCount}/1`, // Veg Counter: mandatory 1 selection at ₹30
      desserts: `${dessertsCount}/1`, // Desserts: mandatory minimum 1 selection with ₹25 base price + item pricing
      settings_staff: `${settingsStaffCount}/5`, // Settings & Staff: mandatory all 5 items selected at ₹30
      allMet: allRequirementsMet
    });
    
    return allRequirementsMet;
  };

  // Fresh juice category mocktail combinations
  const [selectedMocktailCombos, setSelectedMocktailCombos] = useState<string[]>([]);
  
  // Debouncing for preventing double clicks
  const [lastClickTime, setLastClickTime] = useState(0);
  
  const mocktailCombinations = [
    {
      id: 'combo1',
      name: 'Blue Lagoon Combo',
      description: 'Blue Lagoon, Salty Watermelon Frosty & Mojito',
      price: 15
    },
    {
      id: 'combo2', 
      name: 'Strawberry Velvet Combo',
      description: 'Mojito, Salty Watermelon Frosty & Strawberry Velvet',
      price: 15
    },
    {
      id: 'combo3',
      name: 'Pina Colada Combo', 
      description: 'Blue Lagoon, Pina Colada & Mojito',
      price: 15
    }
  ];

  // Calculate total price with special category logic
  // Per-person menu subtotal × guests. Delegates to the SHARED pricing module
  // (catering-order display and server charge must agree). Each selection's
  // `price` equals its menu item price, so base-tier detection is identical.
  const calculateTotalPrice = () => {
    if (!selectedPackage) return 0;
    return computeCateringSubtotal({
      selections: selections.map(s => ({ categoryName: s.categoryName, price: s.price })),
      mocktailCombosCount: selectedMocktailCombos.length,
      guestCount,
    });
  };

  // Handle menu item selection with category-specific limits
  const toggleMenuItemSelection = (category: Category, menuItem: MenuItem) => {
    console.log("🔍 TOGGLE SELECTION DEBUG:", {
      categoryName: category.name,
      menuItemId: menuItem.id,
      menuItemName: menuItem.name,
      currentSelections: selections.length,
      currentStarterSelections: selections.filter(s => s.categoryName === 'starters').length
    });
    
    // Prevent multiple rapid clicks that could cause double selections
    if (Date.now() - lastClickTime < 300) {
      console.log("❌ BLOCKED: Too fast click");
      return;
    }
    setLastClickTime(Date.now());
    
    const existingIndex = selections.findIndex(s => s.menuItemId === menuItem.id);
    console.log("🔍 Existing selection index:", existingIndex);
    
    if (existingIndex >= 0) {
      // Prevent deselection of mandatory Salad Counter
      if (category.name === 'salad_counter') {
        toast({
          title: "Mandatory Item",
          description: "Salad Counter is mandatory and cannot be deselected.",
          variant: "destructive",
        });
        return;
      }

      // Prevent deselection of mandatory Veg Counter
      if (category.name === 'veg_counter') {
        toast({
          title: "Mandatory Item",
          description: "Veg Counter is mandatory and cannot be deselected.",
          variant: "destructive",
        });
        return;
      }
      
      console.log("➖ REMOVING selection");
      // Remove selection
      setSelections(prev => prev.filter((_, index) => index !== existingIndex));
    } else {
      // Check fresh juice category limit (maximum 4 selections)
      if (category.name === 'juices') {
        const currentJuiceSelections = selections.filter(s => s.categoryName === 'juices');
        if (currentJuiceSelections.length >= 4) {
          toast({
            title: "Selection Limit Reached",
            description: "Fresh juice category allows maximum 4 selections for ₹25 base price.",
            variant: "destructive",
          });
          return;
        }
      }
      
      // Check starters category limit - ONLY limit base starters (₹0) to 2 selections, optional sections unlimited
      if (category.name === 'starters') {
        const itemPrice = parseFloat(menuItem.price);
        
        // Only limit base starters (₹0 items) to maximum 2 selections
        if (itemPrice === 0) {
          const currentBaseStarterSelections = selections.filter(s => {
            const selection = categories?.find(cat => cat.name === 'starters')?.menuItems.find(item => item.id === s.menuItemId);
            return s.categoryName === 'starters' && selection && parseFloat(selection.price) === 0;
          });
          console.log("🔍 Current base starter selections before add:", currentBaseStarterSelections.length);
          if (currentBaseStarterSelections.length >= 2) {
            toast({
              title: "Base Starters Limit Reached",
              description: "Maximum 2 base starters allowed for ₹40 base price. You can still add optional premium, vegetarian, or soup starters.",
              variant: "destructive",
            });
            return;
          }
        }
        // Premium, vegetarian, and soup starters (price > 0) have no selection limits
      }

      // Check salad counter category limit (maximum 1 selection)
      if (category.name === 'salad_counter') {
        const currentSaladSelections = selections.filter(s => s.categoryName === 'salad_counter');
        if (currentSaladSelections.length >= 1) {
          toast({
            title: "Selection Limit Reached",
            description: "Salad Counter category allows only 1 selection.",
            variant: "destructive",
          });
          return;
        }
      }

      // Check veg counter category limit (maximum 1 selection)
      if (category.name === 'veg_counter') {
        const currentVegCounterSelections = selections.filter(s => s.categoryName === 'veg_counter');
        if (currentVegCounterSelections.length >= 1) {
          toast({
            title: "Selection Limit Reached",
            description: "Veg Counter category allows only 1 selection.",
            variant: "destructive",
          });
          return;
        }
      }

      // Check breads category limit (maximum 2 selections)
      if (category.name === 'breads') {
        const currentBreadSelections = selections.filter(s => s.categoryName === 'breads');
        if (currentBreadSelections.length >= 2) {
          toast({
            title: "Selection Limit Reached",
            description: "Breads category allows maximum 2 selections for ₹20 base price.",
            variant: "destructive",
          });
          return;
        }
      }
      
      console.log("➕ ADDING new selection");
      // Add selection - single item only
      const newSelection: OrderSelection = {
        categoryId: category.id,
        categoryName: category.name,
        menuItemId: menuItem.id,
        menuItemName: menuItem.name,
        price: menuItem.price
      };
      setSelections(prev => {
        console.log("🔍 Previous selections:", prev.length);
        const newSelections = [...prev, newSelection];
        console.log("🔍 New selections:", newSelections.length);
        return newSelections;
      });
    }
  };

  // Handle mocktail combination selection
  const toggleMocktailCombo = (comboId: string) => {
    setSelectedMocktailCombos(prev => {
      const isSelected = prev.includes(comboId);
      if (isSelected) {
        return prev.filter(id => id !== comboId);
      } else {
        return [...prev, comboId];
      }
    });
  };

  // Submit order
  const handleSubmitOrder = () => {
    if (!selectedPackage || !selectedWedding || !areMinimumRequirementsMet()) {
      toast({
        title: "Cannot Submit Order",
        description: "Please complete all required selections and details.",
        variant: "destructive",
      });
      return;
    }

    const orderData = {
      packageId: selectedPackage.id,
      weddingId: selectedWedding,
      guestCount,
      selections: selections.map(selection => ({
        categoryId: selection.categoryId,
        menuItemId: selection.menuItemId
      })),
      // Mocktail combos are priced separately (₹15/combo/guest) — server needs the count.
      mocktailSelections: selectedMocktailCombos,
      specialRequests,
      deliveryDate
    };

    console.log('Creating final order directly:', orderData);
    createFinalOrderMutation.mutate(orderData);
  };

  // Get category icon
  const getCategoryIcon = (categoryName: string) => {
    switch (categoryName) {
      case 'juices': return <Coffee className="h-4 w-4" />;
      case 'starters': return <ChefHat className="h-4 w-4" />;
      case 'salad_counter': return <Utensils className="h-4 w-4" />;
      case 'breads': return <Utensils className="h-4 w-4" />;
      case 'gravies': return <ChefHat className="h-4 w-4" />;
      case 'rice_dishes': return <Utensils className="h-4 w-4" />;
      case 'live_counters': return <Coffee className="h-4 w-4" />;
      case 'desserts': return <Cake className="h-4 w-4" />;
      case 'settings_staff': return <Settings className="h-4 w-4" />;
      default: return <Utensils className="h-4 w-4" />;
    }
  };

  if (packagesLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-64 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-3 sm:px-6 py-3 sm:py-6 lg:py-8 space-y-4 sm:space-y-6 lg:space-y-8">
      {/* Draft Status Banner */}
      {currentStep === 'menu' && draftOrder && (
        <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 border border-blue-500/30 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-1 sm:space-y-0 sm:space-x-3">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
                <span className="text-blue-300 font-semibold text-sm sm:text-base">Draft Order Active</span>
              </div>
              <div className="text-blue-200 text-xs sm:text-sm">
                Auto-saving your selections every 3 categories
              </div>
            </div>
            <div className="text-blue-200 text-xs sm:text-sm">
              Last saved: {new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center space-y-2 sm:space-y-3 lg:space-y-4">
        <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-white">
          {formatDisplayText("Event")} Catering Orders
        </h1>
        <div className="max-w-3xl mx-auto space-y-1 sm:space-y-2 lg:space-y-3">
          <p className="text-gray-300 text-xs sm:text-sm lg:text-base xl:text-lg leading-relaxed px-2 sm:px-0">
            For the first time, we present online catering menu builder with transparent pricing option.
          </p>
          <p className="text-yellow-400 text-sm sm:text-base lg:text-lg xl:text-xl font-semibold">
            Our Catering Kitchen Partner - Boniyem Royal Chef Caterers
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex justify-center mb-4 sm:mb-6 lg:mb-8 px-2">
        <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-4 overflow-x-auto pb-2 w-full max-w-md sm:max-w-2xl">
          {[
            { key: 'package', label: 'Package', icon: Utensils },
            { key: 'menu', label: 'Menu', icon: ChefHat },
            { key: 'details', label: 'Details', icon: Calendar },
            { key: 'review', label: 'Review', icon: ShoppingCart }
          ].map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.key;
            const isCompleted = ['package', 'menu', 'details', 'review'].indexOf(currentStep) > index;
            
            return (
              <div key={step.key} className="flex items-center flex-shrink-0">
                <div className={`flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 rounded-full border-2 mobile-touch-optimize ${
                  isActive ? 'border-yellow-500 bg-green-500 text-white' :
                  isCompleted ? 'border-green-500 bg-green-500 text-white' :
                  'border-gray-500 bg-gray-700 text-gray-300'
                }`}>
                  {isCompleted ? <Check className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5" /> : <Icon className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />}
                </div>
                <span className={`ml-1 sm:ml-2 text-xs sm:text-sm whitespace-nowrap ${isActive ? 'text-yellow-500' : isCompleted ? 'text-green-500' : 'text-gray-400'}`}>
                  {step.label}
                </span>
                {index < 3 && <div className="w-2 sm:w-4 lg:w-8 h-px bg-gray-600 mx-1 sm:mx-2 lg:mx-4 flex-shrink-0" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step 1: Package Selection */}
      {currentStep === 'package' && (
        <div className="space-y-3 sm:space-y-4 lg:space-y-6">
          <div className="text-center px-3 sm:px-4">
            <div className="flex flex-col sm:flex-row items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-0">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">Select Your Catering Package</h2>
              
              {/* View My Orders Button */}
              <Button 
                onClick={() => window.location.href = '/client/catering/orders'}
                variant="outline"
                className="bg-transparent border-2 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black px-4 sm:px-6 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 mobile-touch-optimize w-full sm:w-auto"
              >
                <ShoppingCart className="h-4 w-4" />
                <span>View My Orders</span>
              </Button>
            </div>
            
            {/* Load Draft Button */}
            {draftOrder && (
              <div className="mb-6 p-4 bg-blue-900/30 border border-blue-600 rounded-lg">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-left">
                    <h3 className="text-blue-300 font-semibold flex items-center">
                      <Save className="h-4 w-4 mr-2" />
                      Draft Order Found
                    </h3>
                    <p className="text-blue-200 text-sm">
                      Last saved: {format(new Date(draftOrder.lastSavedAt), 'PPp')} 
                      • {draftOrder.completedCategories}/12 categories completed
                      • {draftOrder.guestCount} guests
                    </p>
                  </div>
                  <Button 
                    onClick={() => {
                      // Load the draft package and proceed to menu
                      const draftPackage = packages.find((p: CateringPackage) => p.id === draftOrder.packageId);
                      if (draftPackage) {
                        setSelectedPackage(draftPackage);
                        setCurrentStep('menu');
                        setActiveTab('juices'); // Start at first category
                        toast({
                          title: "Draft Loaded",
                          description: "Your saved progress has been restored.",
                        });
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center space-x-2"
                  >
                    <span>Continue Draft Order</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            {packages.map((pkg: CateringPackage) => (
              <Card 
                key={pkg.id} 
                className={`cursor-pointer transition-all duration-200 border-2 mobile-touch-optimize ${
                  selectedPackage?.id === pkg.id 
                    ? 'border-green-500 bg-green-700 text-white' 
                    : 'border-gray-600 bg-gray-800 hover:border-yellow-400'
                }`}
                onClick={() => setSelectedPackage(pkg)}
              >
                <CardHeader className="p-3 sm:p-4 lg:p-6">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-base sm:text-lg text-white">{pkg.name}</CardTitle>
                    {selectedPackage?.id === pkg.id && (
                      <Check className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500" />
                    )}
                  </div>
                  <CardDescription className="text-gray-300 text-xs sm:text-sm">{pkg.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-4 lg:p-6">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center text-yellow-500 font-bold text-lg sm:text-xl">
                      <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5" />
                      {pkg.pricePerPerson}
                    </span>
                    <span className="text-gray-400 text-xs sm:text-sm">per person</span>
                  </div>
                  
                  <div className="flex items-center text-gray-300">
                    <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                    <span className="text-xs sm:text-sm">{pkg.guestRangeMin}-{pkg.guestRangeMax} guests</span>
                  </div>

                  <div className="space-y-2">
                    <h4 className="text-xs sm:text-sm font-semibold text-white">Minimum Selections Required:</h4>
                    <div className="grid grid-cols-2 gap-1 text-xs text-gray-300">
                      <div>• {pkg.minJuices} Juices</div>
                      <div>• {pkg.minStarters} Starters</div>
                      <div>• {pkg.minBreads} Breads</div>
                      <div>• {pkg.minGravies} Gravies</div>
                      <div>• {pkg.minRiceDishes} Rice Dishes</div>
                      <div>• {pkg.minDesserts} Desserts</div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1">
                    {pkg.includesSaladCounter && (
                      <Badge variant="secondary" className="text-xs">✓ Salad Counter</Badge>
                    )}
                    {pkg.includesPremiumSetting && (
                      <Badge variant="secondary" className="text-xs">✓ Premium Setting</Badge>
                    )}
                    {pkg.includesWaterAndStaff && (
                      <Badge variant="secondary" className="text-xs">✓ Water & Staff</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {selectedPackage && (
            <div className="text-center">
              <Button 
                onClick={() => setCurrentStep('menu')}
                className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold px-8 py-3"
              >
                Continue to Menu Selection
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Menu Selection */}
      {currentStep === 'menu' && selectedPackage && (
        <div className="space-y-4 sm:space-y-6">
          <div className="text-center space-y-3 px-4" key={`header-${guestCount}`}>
            <h2 className="text-xl sm:text-2xl font-bold text-white">Build Your Menu</h2>
            <p className="text-gray-300 text-sm sm:text-base">
              Select items for {selectedPackage.name} - Guest Count: {guestCount}
            </p>
            
            {/* Auto-save Status & Quick Payment Access */}
            <div className="flex flex-col items-center space-y-2">
              <div className="flex items-center space-x-2 text-xs">
                {isAutoSaving ? (
                  <div className="flex items-center text-yellow-400">
                    <div className="animate-spin rounded-full h-3 w-3 border border-yellow-400 border-t-transparent mr-1"></div>
                    Saving progress...
                  </div>
                ) : lastSavedAt ? (
                  <div className="flex items-center text-green-400">
                    <Check className="h-3 w-3 mr-1" />
                    Last saved: {format(lastSavedAt, 'HH:mm')}
                    {completedCategories > 0 && (
                      <span className="ml-2 bg-green-600 text-white px-2 py-1 rounded-full text-xs">
                        {completedCategories}/12 categories
                      </span>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
            
            {/* Guest Count Adjuster */}
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4 mt-4">
              <Label htmlFor="guest-count" className="text-white text-sm sm:text-base">Guest Count:</Label>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setGuestCount(Math.max(1, guestCount - 10))}
                  disabled={guestCount <= 1}
                  className="h-9 w-9 p-0 mobile-touch-optimize"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  id="guest-count"
                  type="number"
                  value={guestCount}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 1;
                    setGuestCount(Math.min(selectedPackage.guestRangeMax, Math.max(1, value)));
                  }}
                  className="w-16 sm:w-20 text-center text-base mobile-touch-optimize"
                  min={1}
                  max={selectedPackage.guestRangeMax}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setGuestCount(Math.min(selectedPackage.guestRangeMax, guestCount + 10))}
                  disabled={guestCount >= selectedPackage.guestRangeMax}
                  className="h-9 w-9 p-0 mobile-touch-optimize"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Guest-list nudge: surface the wedding's expected headcount (opt-in, no auto-overwrite) */}
            {(() => {
              const w = weddings.find((x: any) => x.id === selectedWedding) || weddings[0];
              const expected = w?.guestCount || w?.expectedGuests;
              if (!expected || expected === guestCount) return null;
              return (
                <p className="text-xs text-yellow-400/80 text-center mt-3">
                  Your wedding profile lists {expected} expected guests.
                  <button
                    type="button"
                    onClick={() => setGuestCount(Math.min(selectedPackage.guestRangeMax, Math.max(1, expected)))}
                    className="underline ml-1 hover:text-yellow-300"
                  >
                    Use this count
                  </button>
                </p>
              );
            })()}
          </div>

          {/* Package Summary & Progress */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Selected Package Info */}
            <Card className="bg-gradient-to-br from-green-600 via-green-700 to-green-800 text-white lg:col-span-1 shadow-xl border-green-500/30">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between mb-3">
                  <CardTitle className="text-xl font-bold flex items-center">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3">
                      🍽️
                    </div>
                    {selectedPackage.name}
                  </CardTitle>
                  <div className="w-3 h-3 bg-green-300 rounded-full animate-pulse"></div>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <div className="flex items-center justify-center mb-1">
                      <IndianRupee className="h-4 w-4 mr-1" />
                      <span className="font-bold text-lg">{selectedPackage.pricePerPerson}</span>
                    </div>
                    <span className="text-xs text-green-100">per person</span>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <div className="flex items-center justify-center mb-1">
                      <Users className="h-4 w-4 mr-1" />
                      <span className="font-bold text-lg">{guestCount}</span>
                    </div>
                    <span className="text-xs text-green-100">guests</span>
                  </div>
                </div>

                <div className="bg-white/5 rounded-lg p-3 space-y-3">
                  <div className="flex items-center text-sm font-semibold text-green-100">
                    <div className="w-5 h-5 bg-green-400 rounded-full flex items-center justify-center mr-2 text-xs">
                      ✨
                    </div>
                    By LA Mirage Events
                  </div>
                  
                  <div className="space-y-2 text-xs">
                    <div className="flex items-start">
                      <div className="w-4 h-4 bg-green-400/20 rounded-full flex items-center justify-center mr-2 mt-0.5">
                        🤝
                      </div>
                      <div>
                        <div className="text-green-200 font-medium">Catering Partner</div>
                        <div className="text-white font-semibold">Boniyem Royal Chef Caterers</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-4 h-4 bg-green-400/20 rounded-full flex items-center justify-center mr-2 mt-0.5">
                        👨‍💼
                      </div>
                      <div>
                        <div className="text-green-200 font-medium">Catering Manager</div>
                        <div className="text-white font-semibold">John Rich Riju</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center bg-green-500/20 rounded-md p-2">
                      <div className="w-4 h-4 bg-green-400 rounded-full flex items-center justify-center mr-2">
                        📞
                      </div>
                      <div className="font-bold text-white tracking-wide">+91 6282 344 110</div>
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Minimum Requirements Progress */}
            <Card className="bg-gray-800 border-gray-600 lg:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-lg">Selection Progress</CardTitle>
                <p className="text-gray-300 text-sm">Complete minimum requirements to proceed</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(calculateProgress()).map(([categoryKey, req]: [string, any]) => {
                    const isComplete = req.current >= req.required;
                    return (
                      <div key={categoryKey} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center space-x-2">
                            {getCategoryIcon(categoryKey)}
                            <span className="text-sm text-gray-300 capitalize font-medium">
                              {categoryKey.replace('_', ' ')}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            {isComplete && <Check className="h-4 w-4 text-green-500" />}
                            <span className={`text-sm font-semibold ${isComplete ? 'text-green-500' : 'text-yellow-500'}`}>
                              {req.current}/{req.required}
                            </span>
                          </div>
                        </div>
                        <Progress 
                          value={req.progress} 
                          className={`h-2 ${isComplete ? 'bg-green-200' : 'bg-gray-600'}`}
                        />
                      </div>
                    );
                  })}
                </div>
                
                {/* Overall Progress */}
                <div className="mt-4 pt-4 border-t border-gray-600">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">Overall Progress</span>
                    <span className={`font-bold ${areMinimumRequirementsMet() ? 'text-green-500' : 'text-yellow-500'}`}>
                      {Math.round((Object.values(calculateProgress()).reduce((sum: number, req: any) => sum + req.progress, 0) / Object.keys(calculateProgress()).length))}%
                    </span>
                  </div>
                  <Progress 
                    value={Object.values(calculateProgress()).reduce((sum: number, req: any) => sum + req.progress, 0) / Object.keys(calculateProgress()).length} 
                    className="h-3"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Menu Selection Interface */}
          {categoriesLoading ? (
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : (
            <div className="space-y-8">
              {/* Category Navigation */}
              <div className="bg-gray-800 rounded-lg p-3 sm:p-4 lg:p-6">
                <h3 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 text-center">Select Menu Items by Category</h3>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-6 bg-gray-700 h-auto p-2 sm:p-3 gap-2 sm:gap-3">
                    {(() => {
                      // Define the correct business flow order for categories
                      const categoryOrder = ['juices', 'starters', 'salad_counter', 'breads', 'gravies', 'rice_dishes', 'curry_for_rice', 'non_veg_items', 'veg_counter', 'desserts', 'live_counters', 'settings_staff'];
                      
                      // Sort categories according to business flow
                      const sortedCategories = [...categories].sort((a, b) => {
                        const indexA = categoryOrder.indexOf(a.name);
                        const indexB = categoryOrder.indexOf(b.name);
                        return indexA - indexB;
                      });
                      
                      return sortedCategories.map((category: Category) => {
                      const categoryProgress = calculateProgress()[category.name] || { current: 0, required: 0, progress: 0 };
                      const isComplete = categoryProgress.current >= categoryProgress.required;
                      
                      return (
                        <TabsTrigger 
                          key={category.id} 
                          value={category.name}
                          className="flex flex-col items-center p-2 sm:p-3 lg:p-4 space-y-1 sm:space-y-2 data-[state=active]:bg-yellow-600 data-[state=active]:text-white rounded-lg min-h-[60px] sm:min-h-[70px] lg:min-h-[80px] border border-gray-600 hover:border-yellow-400 transition-all duration-200 mobile-touch-optimize"
                        >
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            {getCategoryIcon(category.name)}
                            {isComplete && <Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" />}
                          </div>
                          <div className="text-center">
                            <div className="text-xs font-medium leading-tight">{category.displayName}</div>
                            <div className="text-xs opacity-75 mt-0.5 sm:mt-1">
                              {categoryProgress.current}/{categoryProgress.required}
                            </div>
                          </div>
                        </TabsTrigger>
                      );
                      });
                    })()}
                  </TabsList>

                  {(() => {
                    // Define the correct business flow order for categories
                    const categoryOrder = ['juices', 'starters', 'salad_counter', 'breads', 'gravies', 'rice_dishes', 'curry_for_rice', 'non_veg_items', 'veg_counter', 'desserts', 'live_counters', 'settings_staff'];
                    
                    // Sort categories according to business flow
                    const sortedCategories = [...categories].sort((a, b) => {
                      const indexA = categoryOrder.indexOf(a.name);
                      const indexB = categoryOrder.indexOf(b.name);
                      return indexA - indexB;
                    });
                    
                    return sortedCategories.map((category: Category) => (
                    <TabsContent key={category.id} value={category.name} className="mt-8">
                      <div className="space-y-6">
                        {/* Category Header */}
                        <div className="text-center bg-gray-700 rounded-lg p-6">
                          <div className="flex items-center justify-center space-x-3 mb-3">
                            {getCategoryIcon(category.name)}
                            <h3 className="text-2xl font-bold text-white">
                              {category.displayName}
                              {category.name === 'curry_for_rice' && <span className="text-yellow-400 text-lg ml-2">(Optional)</span>}
                            </h3>
                          </div>
                          {/* Category-specific pricing display */}
                          {category.name === 'juices' ? (
                            <p className="text-gray-300 text-lg mb-4">
                              Fresh juices ₹25 base (max 4 selections) + mocktail combinations ₹15 each
                            </p>
                          ) : category.name === 'starters' ? (
                            <p className="text-gray-300 text-lg mb-4">
                              Appetizers ₹40 base (minimum 1, maximum 2 selections)
                            </p>
                          ) : category.name === 'salad_counter' ? (
                            <p className="text-green-300 text-lg mb-4 font-semibold">
                              ✓ Mandatory Salad Counter ₹25 (automatically included - required for all orders)
                            </p>
                          ) : category.name === 'breads' ? (
                            <p className="text-gray-300 text-lg mb-4">
                              Breads ₹20 base (mandatory 2 selections) • Traditional breads ₹0 • Premium breads +₹10
                            </p>
                          ) : category.name === 'veg_counter' ? (
                            <p className="text-green-300 text-lg mb-4 font-semibold">
                              ✓ Mandatory Veg Counter ₹30 (qty - 10% of pax) • Select exactly 1 from 3 traditional Kerala combination meals
                            </p>
                          ) : category.name === 'desserts' ? (
                            <p className="text-green-300 text-lg mb-4 font-semibold">
                              ✓ Mandatory Desserts ₹25 base (minimum 1 selection) • Base price + individual item pricing (₹15-40)
                            </p>
                          ) : category.name === 'live_counters' ? (
                            <p className="text-yellow-300 text-lg mb-4 font-semibold">
                              Live Counters (individual pricing) • Optional category with 4 specialized counter types
                            </p>
                          ) : category.name === 'settings_staff' ? (
                            <p className="text-green-300 text-lg mb-4 font-semibold">
                              ✓ Mandatory Settings & Staff ₹30 (all 5 items auto-selected) • Final category with comprehensive service package
                            </p>
                          ) : (
                            <p className="text-gray-300 text-lg mb-4">{category.description}</p>
                          )}
                          
                          {/* Category Progress */}
                          <div className="max-w-md mx-auto">
                            {(() => {
                              const categoryProgress = calculateProgress()[category.name] || { current: 0, required: 0, progress: 0 };
                              const isComplete = categoryProgress.current >= categoryProgress.required;
                              return (
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center">
                                    <span className="text-white font-medium">Selection Progress</span>
                                    <span className={`font-bold ${isComplete ? 'text-green-500' : 'text-yellow-500'}`}>
                                      {categoryProgress.current}/{categoryProgress.required} required
                                    </span>
                                  </div>
                                  <div className="w-full bg-gray-600 rounded-full h-3">
                                    <div 
                                      className={`h-3 rounded-full transition-all duration-300 ${
                                        isComplete ? 'bg-green-500' : 'bg-green-500'
                                      }`}
                                      style={{ width: `${categoryProgress.progress}%` }}
                                    />
                                  </div>
                                  {isComplete && (
                                    <p className="text-green-400 text-sm flex items-center justify-center space-x-1">
                                      <Check className="h-4 w-4" />
                                      <span>Minimum requirement completed!</span>
                                    </p>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Non Veg Items Tab-Based Display */}
                        {category.name === 'non_veg_items' ? (
                          <div className="space-y-6">
                            <Tabs defaultValue="chicken" className="w-full">
                              <div className="overflow-x-auto -mx-1 px-1">
                              <TabsList className="inline-flex w-max min-w-full sm:grid sm:w-full sm:grid-cols-7 bg-gray-700 h-auto p-3 gap-2">
                                <TabsTrigger value="chicken" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white text-gray-300 hover:text-white text-sm px-2 py-2 flex-shrink-0">🐔 Chicken</TabsTrigger>
                                <TabsTrigger value="beef" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-gray-300 hover:text-white text-sm px-2 py-2 flex-shrink-0">🥩 Beef</TabsTrigger>
                                <TabsTrigger value="duck" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-gray-300 hover:text-white text-sm px-2 py-2 flex-shrink-0">🦆 Duck</TabsTrigger>
                                <TabsTrigger value="pork" className="data-[state=active]:bg-pink-600 data-[state=active]:text-white text-gray-300 hover:text-white text-sm px-2 py-2 flex-shrink-0">🐷 Pork</TabsTrigger>
                                <TabsTrigger value="mutton" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-gray-300 hover:text-white text-sm px-2 py-2 flex-shrink-0">🐑 Mutton</TabsTrigger>
                                <TabsTrigger value="fish" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-gray-300 hover:text-white text-sm px-2 py-2 flex-shrink-0">🐟 Fish</TabsTrigger>
                                <TabsTrigger value="prawns" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white text-gray-300 hover:text-white text-sm px-2 py-2 flex-shrink-0">🦐 Prawns</TabsTrigger>
                              </TabsList>
                              </div>
                              
                              {['chicken', 'beef', 'duck', 'pork', 'mutton', 'fish', 'prawns'].map(tabCategory => (
                                <TabsContent key={tabCategory} value={tabCategory} className="mt-6">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                                    {category.menuItems
                                      .filter((item: any) => item.tabCategory === tabCategory)
                                      .map((item: MenuItem) => {
                                        const isSelected = selections.some(s => s.menuItemId === item.id);
                                        return (
                                          <div
                                            key={item.id}
                                            className={`relative cursor-pointer transition-all duration-300 transform hover:scale-105 rounded-lg overflow-hidden border-2 ${
                                              isSelected 
                                                ? 'border-golden bg-golden/20 shadow-lg shadow-golden/30' 
                                                : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                                            }`}
                                            onClick={() => toggleMenuItemSelection(category, item)}
                                          >
                                            <div className="p-4 space-y-3">
                                              {/* Selection indicator */}
                                              {isSelected && (
                                                <div className="absolute top-2 right-2 bg-golden rounded-full p-1">
                                                  <Check className="h-4 w-4 text-gray-900" />
                                                </div>
                                              )}
                                              
                                              {/* Item info */}
                                              <div className="space-y-2">
                                                <h4 className="font-semibold text-white text-lg leading-tight">{item.name}</h4>
                                                {item.description && (
                                                  <p className="text-gray-300 text-sm leading-relaxed">{item.description}</p>
                                                )}
                                              </div>
                                              
                                              {/* Price and dietary info */}
                                              <div className="flex items-center justify-between">
                                                <span className="text-golden font-bold text-lg">₹{item.price}</span>
                                                <div className="flex items-center space-x-2">
                                                  <span className="text-red-400 text-sm font-medium">Non-Veg</span>
                                                  {item.isSpicy && <span className="text-red-500">🌶️</span>}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                  </div>
                                </TabsContent>
                              ))}
                            </Tabs>
                          </div>
                        ) : category.name === 'live_counters' ? (
                          <div className="space-y-6">
                            <Tabs defaultValue="thattukada_live" className="w-full">
                              <div className="overflow-x-auto -mx-1 px-1">
                              <TabsList className="inline-flex w-max min-w-full sm:grid sm:w-full sm:grid-cols-4 bg-gray-700 h-auto p-3 gap-2">
                                <TabsTrigger value="thattukada_live" className="data-[state=active]:bg-yellow-600 data-[state=active]:text-white text-gray-300 hover:text-white text-sm px-2 py-2 flex-shrink-0">🍛 Thattukada Live</TabsTrigger>
                                <TabsTrigger value="live_chat_counter" className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-gray-300 hover:text-white text-sm px-2 py-2 flex-shrink-0">💬 Live Chat Counter</TabsTrigger>
                                <TabsTrigger value="live_herbal_tea" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-gray-300 hover:text-white text-sm px-2 py-2 flex-shrink-0">🌿 Live Herbal Tea</TabsTrigger>
                                <TabsTrigger value="live_tea_counter" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-gray-300 hover:text-white text-sm px-2 py-2 flex-shrink-0">☕ Live Tea Counter</TabsTrigger>
                              </TabsList>
                              </div>
                              
                              {['thattukada_live', 'live_chat_counter', 'live_herbal_tea', 'live_tea_counter'].map(tabCategory => (
                                <TabsContent key={tabCategory} value={tabCategory} className="mt-6">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                                    {category.menuItems
                                      .filter((item: any) => item.tabCategory === tabCategory)
                                      .map((item: MenuItem) => {
                                        const isSelected = selections.some(s => s.menuItemId === item.id);
                                        return (
                                          <div
                                            key={item.id}
                                            className={`relative cursor-pointer transition-all duration-300 transform hover:scale-105 rounded-lg overflow-hidden border-2 ${
                                              isSelected 
                                                ? 'border-golden bg-golden/20 shadow-lg shadow-golden/30' 
                                                : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                                            }`}
                                            onClick={() => toggleMenuItemSelection(category, item)}
                                          >
                                            <div className="p-4 space-y-3">
                                              {/* Selection indicator */}
                                              {isSelected && (
                                                <div className="absolute top-2 right-2 bg-golden rounded-full p-1">
                                                  <Check className="h-4 w-4 text-gray-900" />
                                                </div>
                                              )}
                                              
                                              {/* Item info */}
                                              <div className="space-y-2">
                                                <h4 className="font-semibold text-white text-lg leading-tight">{item.name}</h4>
                                                {item.description && (
                                                  <p className="text-gray-300 text-sm leading-relaxed">{item.description}</p>
                                                )}
                                              </div>
                                              
                                              {/* Price and dietary info */}
                                              <div className="flex items-center justify-between">
                                                <span className="text-golden font-bold text-lg">₹{item.price}</span>
                                                <div className="flex items-center space-x-2">
                                                  <span className={`text-sm font-medium ${item.isVegetarian ? 'text-green-400' : 'text-red-400'}`}>
                                                    {item.isVegetarian ? 'Veg' : 'Non-Veg'}
                                                  </span>
                                                  {item.isSpicy && <span className="text-red-500">🌶️</span>}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                  </div>
                                  {category.menuItems.filter((item: any) => item.tabCategory === tabCategory).length === 0 && (
                                    <div className="text-center py-12">
                                      <Coffee className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                                      <p className="text-gray-400 text-lg">No items available in this counter yet</p>
                                      <p className="text-gray-500 text-sm mt-2">Items will be added soon for this live counter section</p>
                                    </div>
                                  )}
                                </TabsContent>
                              ))}
                            </Tabs>
                          </div>
                        ) : category.name === 'settings_staff' ? (
                          /* Settings & Staff - 6-column layout with special handling */
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 sm:gap-6">
                            {category.menuItems.map((item: MenuItem) => {
                              const isSelected = selections.some(s => s.menuItemId === item.id);
                              const isMandatory = true; // All Settings & Staff items are mandatory
                              
                              return (
                                <Card 
                                  key={item.id}
                                  className="border-2 border-green-500 bg-green-900/30 shadow-green-500/25 cursor-not-allowed opacity-90"
                                  onClick={(e) => {
                                    e.preventDefault(); // Prevent deselection of mandatory items
                                    return false;
                                  }}
                                >
                                  <CardContent className="p-4 sm:p-5">
                                    {/* Header with Selection Indicator */}
                                    <div className="flex justify-between items-start mb-3">
                                      <h4 className="font-bold text-white text-base leading-tight">{item.name}</h4>
                                      <div className="bg-green-500 rounded-full p-1">
                                        <Check className="h-4 w-4 text-white" />
                                      </div>
                                    </div>
                                    
                                    {/* Description */}
                                    <p className="text-gray-300 text-sm mb-4 line-clamp-3 leading-relaxed">
                                      {item.description}
                                    </p>
                                    
                                    {/* Price - Show ₹0 for individual items */}
                                    <div className="mb-4">
                                      <span className="flex items-center text-yellow-500 font-bold text-lg">
                                        <IndianRupee className="h-4 w-4" />
                                        0
                                        <span className="text-gray-400 text-sm ml-2">/person</span>
                                      </span>
                                    </div>
                                    
                                    {/* Badges */}
                                    <div className="flex flex-wrap gap-2">
                                      <Badge className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold">
                                        ✓ Mandatory
                                      </Badge>
                                      <Badge className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
                                        Service
                                      </Badge>
                                    </div>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        ) : category.name !== 'non_veg_items' && category.name !== 'live_counters' && (
                          /* Regular Menu Items Grid - Filter out premium starters for base section */
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                            {(() => {
                              // For starters category, only show base items (price = 0) in main grid
                              const itemsToShow = category.name === 'starters' 
                                ? (() => {
                                    console.log('🔍 STARTERS DEBUG - All items:', category.menuItems.map(item => ({ name: item.name, price: item.price })));
                                    const filteredItems = category.menuItems.filter(item => parseFloat(item.price) === 0);
                                    console.log('🔍 STARTERS DEBUG - Filtered ₹0 items with parseFloat:', filteredItems.map(item => ({ name: item.name, price: item.price })));
                                    return filteredItems;
                                  })()
                                : category.menuItems;
                              
                              return itemsToShow.map((item: MenuItem) => {
                                const isSelected = selections.some(s => s.menuItemId === item.id);
                                const isMandatory = category.name === 'salad_counter';
                              
                                return (
                              <Card 
                                key={item.id}
                                className={`transition-all duration-300 border-2 hover:shadow-lg mobile-touch-optimize ${
                                  isMandatory 
                                    ? 'border-green-500 bg-green-900/30 shadow-green-500/25 cursor-not-allowed opacity-90' 
                                    : isSelected 
                                      ? 'border-green-500 bg-green-50 dark:bg-green-900/30 shadow-green-500/25 cursor-pointer transform hover:scale-105' 
                                      : 'border-gray-600 bg-gray-800 hover:border-yellow-400 hover:bg-gray-750 cursor-pointer transform hover:scale-105'
                                }`}
                                onClick={() => toggleMenuItemSelection(category, item)}
                              >
                                <CardContent className="p-4 sm:p-5">
                                  {/* Header with Selection Indicator */}
                                  <div className="flex justify-between items-start mb-3">
                                    <h4 className="font-bold text-white text-base leading-tight">{item.name}</h4>
                                    {isMandatory ? (
                                      <div className="bg-green-500 rounded-full p-1">
                                        <Check className="h-4 w-4 text-white" />
                                      </div>
                                    ) : isSelected && (
                                      <div className="bg-green-500 rounded-full p-1">
                                        <Check className="h-4 w-4 text-white" />
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Description */}
                                  <p className="text-gray-300 text-sm mb-4 line-clamp-3 leading-relaxed">
                                    {item.description}
                                  </p>
                                  
                                  {/* Price */}
                                  <div className="mb-4">
                                    <span className="flex items-center text-yellow-500 font-bold text-lg">
                                      <IndianRupee className="h-4 w-4" />
                                      {category.name === 'settings_staff' ? '0' : item.price}
                                      {item.servingSize && (
                                        <span className="text-gray-400 text-sm ml-2">/{item.servingSize}</span>
                                      )}
                                    </span>
                                  </div>
                                  
                                  {/* Badges */}
                                  <div className="flex flex-wrap gap-2">
                                    {isMandatory && (
                                      <Badge className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold">
                                        ✓ Required
                                      </Badge>
                                    )}
                                    {(() => {
                                      // Determine if item is actually vegetarian based on name content
                                      const isActuallyVegetarian = !item.name.toLowerCase().includes('chicken') &&
                                                                   !item.name.toLowerCase().includes('meat') &&
                                                                   !item.name.toLowerCase().includes('beef') &&
                                                                   !item.name.toLowerCase().includes('prawns') &&
                                                                   !item.name.toLowerCase().includes('seafood') &&
                                                                   !item.name.toLowerCase().includes('fish');
                                      
                                      return isActuallyVegetarian && (
                                        <Badge className="bg-green-600 hover:bg-green-700 text-white text-xs">
                                          Vegetarian
                                        </Badge>
                                      );
                                    })()}
                                    {item.isSpicy && (
                                      <Badge className="bg-red-600 hover:bg-red-700 text-white text-xs">
                                        🌶️ Spicy
                                      </Badge>
                                    )}
                                    {item.preparationTime && (
                                      <Badge variant="outline" className="text-xs border-gray-500">
                                        <Clock className="h-3 w-3 mr-1" />
                                        {item.preparationTime}m
                                      </Badge>
                                    )}
                                  </div>
                                </CardContent>
                              </Card>
                                );
                              });
                            })()}
                          </div>
                        )}

                        {/* Starters Category - Premium Addition Items */}
                        {category.name === 'starters' && (
                          <div className="bg-gradient-to-br from-orange-900 to-red-900 rounded-lg p-6 mt-6">
                            <div className="text-center mb-6">
                              <h3 className="text-2xl font-bold text-white mb-2">🍗 Premium Starters Addition (Optional)</h3>
                              <p className="text-orange-200 text-sm">
                                Add optional premium starters to your base selection with individual pricing
                              </p>
                              <div className="bg-orange-800/50 rounded-lg p-3 mt-3">
                                <p className="text-white font-semibold">
                                  💡 Pricing: Base Starters (1-2 selections) = ₹40 | Premium Additions = Individual prices
                                </p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                              {(() => {
                                // Filter premium addition items (items with price >= 60 in starters category)
                                const premiumAdditionItems = category.menuItems.filter(item => item.price >= 60);
                                
                                return premiumAdditionItems.map((item) => {
                                  const isSelected = selections.some(s => s.menuItemId === item.id);
                                  
                                  return (
                                    <Card 
                                      key={item.id}
                                      className={`cursor-pointer transition-all duration-300 border-2 hover:shadow-lg transform hover:scale-105 ${
                                        isSelected 
                                          ? 'border-green-500 bg-green-50 dark:bg-green-900/30 shadow-green-500/25' 
                                          : 'border-orange-600 bg-orange-800/30 hover:border-yellow-400 hover:bg-orange-700/50'
                                      }`}
                                      onClick={() => toggleMenuItemSelection(category, item)}
                                    >
                                      <CardContent className="p-4">
                                        <div className="flex justify-between items-start mb-3">
                                          <h4 className="font-bold text-white text-sm leading-tight">{item.name}</h4>
                                          {isSelected && (
                                            <div className="bg-green-500 rounded-full p-1">
                                              <Check className="h-3 w-3 text-white" />
                                            </div>
                                          )}
                                        </div>
                                        
                                        <p className="text-orange-200 text-xs mb-3 leading-relaxed">
                                          {item.description}
                                        </p>
                                        
                                        <div className="flex justify-between items-center">
                                          <span className="flex items-center text-yellow-500 font-bold text-sm">
                                            <IndianRupee className="h-3 w-3" />
                                            {item.price}/person
                                          </span>
                                          <Badge className="bg-orange-600 hover:bg-orange-700 text-white text-xs">
                                            Premium
                                          </Badge>
                                        </div>
                                      </CardContent>
                                    </Card>
                                  );
                                });
                              })()}
                            </div>

                            {(() => {
                              const selectedPremiumAdditions = selections.filter(s => {
                                const item = category.menuItems.find(mi => mi.id === s.menuItemId);
                                return item && item.price >= 60;
                              });
                              
                              if (selectedPremiumAdditions.length > 0) {
                                return (
                                  <div className="bg-orange-800/50 rounded-lg p-4 mt-4">
                                    <h4 className="text-white font-semibold mb-2 flex items-center">
                                      <Check className="h-4 w-4 text-green-500 mr-2" />
                                      Selected Premium Additions ({selectedPremiumAdditions.length})
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                      {selectedPremiumAdditions.map(selection => {
                                        const item = category.menuItems.find(mi => mi.id === selection.menuItemId);
                                        return (
                                          <Badge key={selection.menuItemId} variant="secondary" className="bg-yellow-600 text-white">
                                            {item?.name} (₹{item?.price})
                                          </Badge>
                                        );
                                      })}
                                    </div>
                                    <p className="text-orange-200 text-sm mt-2">
                                      Additional cost: ₹{selectedPremiumAdditions.reduce((total, selection) => {
                                        const item = category.menuItems.find(mi => mi.id === selection.menuItemId);
                                        return total + (item?.price || 0);
                                      }, 0)} per person
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}

                        {/* Veg Starters Category - Vegetarian Options */}
                        {category.name === 'starters' && (
                          <div className="bg-gradient-to-br from-green-900 to-emerald-900 rounded-lg p-6 mt-6">
                            <div className="text-center mb-6">
                              <h3 className="text-2xl font-bold text-white mb-2">🥗 Veg Starters (Optional)</h3>
                              <p className="text-green-200 text-sm">
                                Add fresh vegetarian starters to your selection with individual pricing
                              </p>
                              <div className="bg-green-800/50 rounded-lg p-3 mt-3">
                                <p className="text-white font-semibold">
                                  💡 Pricing: Fresh vegetarian options at ₹15-20 each | Perfect for all dietary preferences
                                </p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                              {(() => {
                                // Filter veg starter items (vegetarian items with price 15-25, exclude soups)
                                const vegStarterItems = category.menuItems.filter(item => 
                                  item.price >= 15 && item.price <= 25 && 
                                  !item.name.toLowerCase().includes('soup') &&
                                  (item.name.toLowerCase().includes('veg') ||
                                   item.name.toLowerCase().includes('momos') ||
                                   item.name.toLowerCase().includes('cutlet') ||
                                   item.name.toLowerCase().includes('cheese') ||
                                   item.name.toLowerCase().includes('fries') ||
                                   item.name.toLowerCase().includes('spring'))
                                );
                                
                                return vegStarterItems.map((item) => {
                                  const isSelected = selections.some(s => s.menuItemId === item.id);
                                  
                                  return (
                                    <Card 
                                      key={item.id}
                                      className={`cursor-pointer transition-all duration-300 border-2 hover:shadow-lg transform hover:scale-105 ${
                                        isSelected 
                                          ? 'border-green-500 bg-green-50 dark:bg-green-900/30 shadow-green-500/25' 
                                          : 'border-green-600 bg-green-800/30 hover:border-yellow-400 hover:bg-green-700/50'
                                      }`}
                                      onClick={() => toggleMenuItemSelection(category, item)}
                                    >
                                      <CardContent className="p-4">
                                        <div className="flex justify-between items-start mb-3">
                                          <h4 className="font-bold text-white text-sm leading-tight">{item.name}</h4>
                                          {isSelected && (
                                            <div className="bg-green-500 rounded-full p-1">
                                              <Check className="h-3 w-3 text-white" />
                                            </div>
                                          )}
                                        </div>
                                        
                                        <p className="text-green-200 text-xs mb-3 leading-relaxed">
                                          {item.description}
                                        </p>
                                        
                                        <div className="flex justify-between items-center">
                                          <span className="flex items-center text-yellow-500 font-bold text-sm">
                                            <IndianRupee className="h-3 w-3" />
                                            {item.price}/person
                                          </span>
                                          <Badge className="bg-green-600 hover:bg-green-700 text-white text-xs">
                                            Vegetarian
                                          </Badge>
                                        </div>
                                      </CardContent>
                                    </Card>
                                  );
                                });
                              })()}
                            </div>

                            {(() => {
                              const selectedVegStarters = selections.filter(s => {
                                const item = category.menuItems.find(mi => mi.id === s.menuItemId);
                                return item && item.price >= 15 && item.price <= 25;
                              });
                              
                              if (selectedVegStarters.length > 0) {
                                return (
                                  <div className="bg-green-800/50 rounded-lg p-4 mt-4">
                                    <h4 className="text-white font-semibold mb-2 flex items-center">
                                      <Check className="h-4 w-4 text-green-500 mr-2" />
                                      Selected Veg Starters ({selectedVegStarters.length})
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                      {selectedVegStarters.map(selection => {
                                        const item = category.menuItems.find(mi => mi.id === selection.menuItemId);
                                        return (
                                          <Badge key={selection.menuItemId} variant="secondary" className="bg-yellow-600 text-white">
                                            {item?.name} (₹{item?.price})
                                          </Badge>
                                        );
                                      })}
                                    </div>
                                    <p className="text-green-200 text-sm mt-2">
                                      Additional cost: ₹{selectedVegStarters.reduce((total, selection) => {
                                        const item = category.menuItems.find(mi => mi.id === selection.menuItemId);
                                        return total + (item?.price || 0);
                                      }, 0)} per person
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}

                        {/* Soup Starters Category - Hot Soup Options */}
                        {category.name === 'starters' && (
                          <div className="bg-gradient-to-br from-blue-900 to-indigo-900 rounded-lg p-6 mt-6">
                            <div className="text-center mb-6">
                              <h3 className="text-2xl font-bold text-white mb-2">🍲 Soup Starters (Optional)</h3>
                              <p className="text-blue-200 text-sm">
                                Add hot soup starters to your selection with individual pricing
                              </p>
                              <div className="bg-blue-800/50 rounded-lg p-3 mt-3">
                                <p className="text-white font-semibold">
                                  💡 Pricing: Chicken/Seafood soups ₹20 each | Vegetarian soups ₹15 each
                                </p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                              {(() => {
                                // Filter soup starter items (items with price 15-20 but not in veg starters range and containing "soup" or specific soup names)
                                const soupStarterItems = category.menuItems.filter(item => 
                                  (item.price >= 15 && item.price <= 20) && 
                                  (item.name.toLowerCase().includes('soup') || 
                                   item.name.toLowerCase().includes('thai') ||
                                   item.name.toLowerCase().includes('cream of') ||
                                   item.name.toLowerCase().includes('lemon grass') ||
                                   item.name.toLowerCase().includes('manchow')) &&
                                  !['veg cutlet', 'steamed momos', 'veg nuggets', 'cheese balls', 'french fries', 'veg spring rolls'].includes(item.name.toLowerCase())
                                );
                                
                                return soupStarterItems.map((item) => {
                                  const isSelected = selections.some(s => s.menuItemId === item.id);
                                  const isChickenSeafood = item.price === 20;
                                  
                                  return (
                                    <Card 
                                      key={item.id}
                                      className={`cursor-pointer transition-all duration-300 border-2 hover:shadow-lg transform hover:scale-105 ${
                                        isSelected 
                                          ? 'border-green-500 bg-green-50 dark:bg-green-900/30 shadow-green-500/25' 
                                          : 'border-blue-600 bg-blue-800/30 hover:border-yellow-400 hover:bg-blue-700/50'
                                      }`}
                                      onClick={() => toggleMenuItemSelection(category, item)}
                                    >
                                      <CardContent className="p-4">
                                        <div className="flex justify-between items-start mb-3">
                                          <h4 className="font-bold text-white text-sm leading-tight">{item.name}</h4>
                                          {isSelected && (
                                            <div className="bg-green-500 rounded-full p-1">
                                              <Check className="h-3 w-3 text-white" />
                                            </div>
                                          )}
                                        </div>
                                        
                                        <p className="text-blue-200 text-xs mb-3 leading-relaxed">
                                          {item.description}
                                        </p>
                                        
                                        <div className="flex justify-between items-center">
                                          <span className="flex items-center text-yellow-500 font-bold text-sm">
                                            <IndianRupee className="h-3 w-3" />
                                            {item.price}/person
                                          </span>
                                          {(() => {
                                            // Determine if soup is actually vegetarian based on name content
                                            const isActuallyVegetarian = !item.name.toLowerCase().includes('chicken') &&
                                                                         !item.name.toLowerCase().includes('meat') &&
                                                                         !item.name.toLowerCase().includes('beef') &&
                                                                         !item.name.toLowerCase().includes('prawns') &&
                                                                         !item.name.toLowerCase().includes('seafood') &&
                                                                         !item.name.toLowerCase().includes('fish');
                                            
                                            return (
                                              <Badge className={`text-white text-xs ${
                                                isActuallyVegetarian 
                                                  ? 'bg-green-600 hover:bg-green-700' 
                                                  : 'bg-red-600 hover:bg-red-700'
                                              }`}>
                                                {isActuallyVegetarian ? 'Vegetarian' : 'Non-Veg'}
                                              </Badge>
                                            );
                                          })()}
                                        </div>
                                      </CardContent>
                                    </Card>
                                  );
                                });
                              })()}
                            </div>

                            {(() => {
                              const selectedSoupStarters = selections.filter(s => {
                                const item = category.menuItems.find(mi => mi.id === s.menuItemId);
                                return item && 
                                  (item.price >= 15 && item.price <= 20) && 
                                  (item.name.toLowerCase().includes('soup') || 
                                   item.name.toLowerCase().includes('thai') ||
                                   item.name.toLowerCase().includes('cream of') ||
                                   item.name.toLowerCase().includes('lemon grass') ||
                                   item.name.toLowerCase().includes('manchow')) &&
                                  !['veg cutlet', 'steamed momos', 'veg nuggets', 'cheese balls', 'french fries', 'veg spring rolls'].includes(item.name.toLowerCase());
                              });
                              
                              if (selectedSoupStarters.length > 0) {
                                return (
                                  <div className="bg-blue-800/50 rounded-lg p-4 mt-4">
                                    <h4 className="text-white font-semibold mb-2 flex items-center">
                                      <Check className="h-4 w-4 text-green-500 mr-2" />
                                      Selected Soup Starters ({selectedSoupStarters.length})
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                      {selectedSoupStarters.map(selection => {
                                        const item = category.menuItems.find(mi => mi.id === selection.menuItemId);
                                        return (
                                          <Badge key={selection.menuItemId} variant="secondary" className="bg-yellow-600 text-white">
                                            {item?.name} (₹{item?.price})
                                          </Badge>
                                        );
                                      })}
                                    </div>
                                    <p className="text-blue-200 text-sm mt-2">
                                      Additional cost: ₹{selectedSoupStarters.reduce((total, selection) => {
                                        const item = category.menuItems.find(mi => mi.id === selection.menuItemId);
                                        return total + (item?.price || 0);
                                      }, 0)} per person
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}

                        {/* Fresh Juice Category - Mocktail Combinations */}
                        {category.name === 'juices' && (
                          <div className="bg-gradient-to-br from-blue-900 to-purple-900 rounded-lg p-6 mt-6">
                            <div className="text-center mb-6">
                              <h3 className="text-2xl font-bold text-white mb-2">🧊 Premium Mocktail Combinations (Optional)</h3>
                              <p className="text-blue-200 text-sm">
                                Add optional premium mocktail combinations to your fresh juice selection for ₹15 each
                              </p>
                              <div className="bg-blue-800/50 rounded-lg p-3 mt-3">
                                <p className="text-white font-semibold">
                                  💡 Pricing: Fresh Juices (max 4) = ₹25 | Each Mocktail Combo = +₹15
                                </p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {mocktailCombinations.map((combo) => {
                                const isSelected = selectedMocktailCombos.includes(combo.id);
                                
                                return (
                                  <Card 
                                    key={combo.id}
                                    className={`cursor-pointer transition-all duration-300 border-2 hover:shadow-lg transform hover:scale-105 ${
                                      isSelected 
                                        ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/30 shadow-yellow-500/25' 
                                        : 'border-blue-600 bg-blue-800/30 hover:border-yellow-400 hover:bg-blue-700/50'
                                    }`}
                                    onClick={() => toggleMocktailCombo(combo.id)}
                                  >
                                    <CardContent className="p-4">
                                      <div className="flex justify-between items-start mb-3">
                                        <h4 className="font-bold text-white text-sm leading-tight">{combo.name}</h4>
                                        {isSelected && (
                                          <div className="bg-green-500 rounded-full p-1">
                                            <Check className="h-3 w-3 text-white" />
                                          </div>
                                        )}
                                      </div>
                                      
                                      <p className="text-blue-200 text-xs mb-3 leading-relaxed">
                                        {combo.description}
                                      </p>
                                      
                                      <div className="flex justify-between items-center">
                                        <span className="flex items-center text-yellow-500 font-bold text-sm">
                                          <IndianRupee className="h-3 w-3" />
                                          {combo.price}/person
                                        </span>
                                        <Badge className="bg-purple-600 hover:bg-purple-700 text-white text-xs">
                                          Premium
                                        </Badge>
                                      </div>
                                    </CardContent>
                                  </Card>
                                );
                              })}
                            </div>

                            {selectedMocktailCombos.length > 0 && (
                              <div className="bg-purple-800/50 rounded-lg p-4 mt-4">
                                <h4 className="text-white font-semibold mb-2 flex items-center">
                                  <Check className="h-4 w-4 text-green-500 mr-2" />
                                  Selected Mocktail Combinations ({selectedMocktailCombos.length})
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {selectedMocktailCombos.map(comboId => {
                                    const combo = mocktailCombinations.find(c => c.id === comboId);
                                    return (
                                      <Badge key={comboId} variant="secondary" className="bg-yellow-600 text-white">
                                        {combo?.name}
                                      </Badge>
                                    );
                                  })}
                                </div>
                                <p className="text-purple-200 text-sm mt-2">
                                  Additional cost: ₹{selectedMocktailCombos.length * 15} per person
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Category Summary with Next Category Navigation */}
                        {(() => {
                          const categorySelections = selections.filter(s => 
                            category.menuItems.some(item => item.id === s.menuItemId)
                          );
                          
                          // Check if current category requirements are met
                          const progress = calculateProgress();
                          const currentCategoryProgress = progress[category.name];
                          // For optional categories (curry_for_rice, non_veg_items, live_counters), always consider them complete
                          const isOptionalCategory = category.name === 'curry_for_rice' || category.name === 'non_veg_items' || category.name === 'live_counters';
                          const isCategoryComplete = isOptionalCategory || (currentCategoryProgress && currentCategoryProgress.current >= currentCategoryProgress.required);
                          
                          // Find next category in business flow order (for transparency, show all categories)
                          const categoryOrder = ['juices', 'starters', 'salad_counter', 'breads', 'gravies', 'rice_dishes', 'curry_for_rice', 'non_veg_items', 'veg_counter', 'desserts', 'live_counters', 'settings_staff'];
                          const currentIndex = categoryOrder.indexOf(category.name);
                          const nextCategoryName = currentIndex >= 0 && currentIndex < categoryOrder.length - 1 
                            ? categoryOrder[currentIndex + 1] 
                            : null;
                          const nextCategory = nextCategoryName ? categories.find(cat => cat.name === nextCategoryName) : null;
                          
                          // Show navigation for categories with selections OR for optional categories (curry_for_rice, non_veg_items)
                          if (categorySelections.length > 0 || isOptionalCategory) {
                            return (
                              <div className="bg-gray-700 rounded-lg p-4 mt-6">
                                <h4 className="text-white font-semibold mb-2 flex items-center">
                                  {categorySelections.length > 0 ? (
                                    <>
                                      <Check className="h-4 w-4 text-green-500 mr-2" />
                                      Selected {category.displayName} ({categorySelections.length} items)
                                    </>
                                  ) : (
                                    <>
                                      <Check className="h-4 w-4 text-green-500 mr-2" />
                                      {category.displayName} (Optional - No items selected)
                                    </>
                                  )}
                                  {category.name === 'juices' && (
                                    <span className="ml-2 text-yellow-500 text-sm">
                                      (₹25 base price for up to 4 selections)
                                    </span>
                                  )}
                                  {category.name === 'starters' && (() => {
                                    // Calculate starters pricing breakdown
                                    const starterSelections = categorySelections;
                                    const baseStarters = starterSelections.filter(s => {
                                      const starterItem = category.menuItems.find(item => item.id === s.menuItemId);
                                      return starterItem && parseFloat(starterItem.price) === 0;
                                    });
                                    const premiumStarters = starterSelections.filter(s => {
                                      const starterItem = category.menuItems.find(item => item.id === s.menuItemId);
                                      return starterItem && parseFloat(starterItem.price) > 0;
                                    });
                                    
                                    const basePrice = baseStarters.length > 0 ? 40 : 0;
                                    const premiumPrice = premiumStarters.reduce((total, s) => {
                                      const starterItem = category.menuItems.find(item => item.id === s.menuItemId);
                                      return total + (starterItem ? parseFloat(starterItem.price) : 0);
                                    }, 0);
                                    
                                    return (
                                      <span className="ml-2 text-yellow-500 text-sm">
                                        (₹{basePrice + premiumPrice} total: ₹{basePrice} base + ₹{premiumPrice} optional)
                                      </span>
                                    );
                                  })()}
                                  {category.name === 'breads' && (() => {
                                    // Calculate breads pricing breakdown
                                    const breadSelections = categorySelections;
                                    const premiumBreads = breadSelections.filter(s => {
                                      const breadItem = category.menuItems.find(item => item.id === s.menuItemId);
                                      return breadItem && parseFloat(breadItem.price) > 0;
                                    });
                                    
                                    const basePrice = 20; // Always ₹20 base for breads category
                                    const premiumPrice = premiumBreads.reduce((total, s) => {
                                      const breadItem = category.menuItems.find(item => item.id === s.menuItemId);
                                      return total + (breadItem ? parseFloat(breadItem.price) : 0);
                                    }, 0);
                                    
                                    return (
                                      <span className="ml-2 text-yellow-500 text-sm">
                                        (₹{basePrice + premiumPrice} total: ₹{basePrice} base + ₹{premiumPrice} premium)
                                      </span>
                                    );
                                  })()}
                                  {category.name === 'gravies' && (() => {
                                    // Calculate gravies pricing breakdown - individual item pricing only
                                    const graviesSelections = categorySelections;
                                    const totalPrice = graviesSelections.reduce((total, s) => {
                                      const gravyItem = category.menuItems.find(item => item.id === s.menuItemId);
                                      return total + (gravyItem ? parseFloat(gravyItem.price) : 0);
                                    }, 0);
                                    
                                    return (
                                      <span className="ml-2 text-yellow-500 text-sm">
                                        (₹{totalPrice} total: individual pricing)
                                      </span>
                                    );
                                  })()}
                                  {category.name === 'rice_dishes' && (() => {
                                    // Calculate rice dishes pricing breakdown - individual item pricing only
                                    const riceDishesSelections = categorySelections;
                                    const totalPrice = riceDishesSelections.reduce((total, s) => {
                                      const riceItem = category.menuItems.find(item => item.id === s.menuItemId);
                                      return total + (riceItem ? parseFloat(riceItem.price) : 0);
                                    }, 0);
                                    
                                    return (
                                      <span className="ml-2 text-yellow-500 text-sm">
                                        (₹{totalPrice} total: individual pricing)
                                      </span>
                                    );
                                  })()}
                                  {category.name === 'curry_for_rice' && (() => {
                                    // Calculate curry for rice pricing breakdown - individual item pricing only
                                    const curryForRiceSelections = categorySelections;
                                    const totalPrice = curryForRiceSelections.reduce((total, s) => {
                                      const curryItem = category.menuItems.find(item => item.id === s.menuItemId);
                                      return total + (curryItem ? parseFloat(curryItem.price) : 0);
                                    }, 0);
                                    
                                    return (
                                      <span className="ml-2 text-yellow-500 text-sm">
                                        (₹{totalPrice} total: individual pricing)
                                      </span>
                                    );
                                  })()}
                                  {category.name === 'non_veg_items' && (() => {
                                    // Calculate non veg items pricing breakdown - individual item pricing only
                                    const nonVegItemsSelections = categorySelections;
                                    const totalPrice = nonVegItemsSelections.reduce((total, s) => {
                                      const nonVegItem = category.menuItems.find(item => item.id === s.menuItemId);
                                      return total + (nonVegItem ? parseFloat(nonVegItem.price) : 0);
                                    }, 0);
                                    
                                    return (
                                      <span className="ml-2 text-yellow-500 text-sm">
                                        (₹{totalPrice} total: individual pricing)
                                      </span>
                                    );
                                  })()}
                                  {category.name === 'veg_counter' && (() => {
                                    // Calculate veg counter pricing breakdown - base price ₹30 for mandatory single selection
                                    const vegCounterSelections = categorySelections;
                                    const totalPrice = vegCounterSelections.length > 0 ? 30 : 0;
                                    
                                    return (
                                      <span className="ml-2 text-yellow-500 text-sm">
                                        (₹{totalPrice} total: base pricing)
                                      </span>
                                    );
                                  })()}
                                  {category.name === 'desserts' && (() => {
                                    // Calculate desserts pricing breakdown - ₹25 base price + individual item pricing
                                    const dessertsSelections = categorySelections;
                                    const basePrice = dessertsSelections.length > 0 ? 25 : 0;
                                    const itemPrices = dessertsSelections.reduce((total, s) => {
                                      const dessertItem = category.menuItems.find(item => item.id === s.menuItemId);
                                      return total + (dessertItem ? parseFloat(dessertItem.price) : 0);
                                    }, 0);
                                    const totalPrice = basePrice + itemPrices;
                                    
                                    return (
                                      <span className="ml-2 text-yellow-500 text-sm">
                                        (₹{totalPrice} total: ₹{basePrice} base + ₹{itemPrices} items)
                                      </span>
                                    );
                                  })()}
                                  {category.name === 'live_counters' && (() => {
                                    // Calculate live counters pricing breakdown - individual item pricing only
                                    const liveCountersSelections = categorySelections;
                                    const totalPrice = liveCountersSelections.reduce((total, s) => {
                                      const liveCounterItem = category.menuItems.find(item => item.id === s.menuItemId);
                                      return total + (liveCounterItem ? parseFloat(liveCounterItem.price) : 0);
                                    }, 0);
                                    
                                    return (
                                      <span className="ml-2 text-yellow-500 text-sm">
                                        (₹{totalPrice} total: individual pricing)
                                      </span>
                                    );
                                  })()}
                                  {category.name === 'settings_staff' && (() => {
                                    // Calculate settings & staff pricing breakdown - ₹30 base price (auto-selected)
                                    const settingsStaffSelections = categorySelections;
                                    const basePrice = 30; // Always ₹30 base for settings & staff category
                                    const itemPrices = settingsStaffSelections.reduce((total, s) => {
                                      const settingsItem = category.menuItems.find(item => item.id === s.menuItemId);
                                      return total + (settingsItem ? parseFloat(settingsItem.price) : 0);
                                    }, 0);
                                    const totalPrice = basePrice + itemPrices;
                                    
                                    return (
                                      <span className="ml-2 text-yellow-500 text-sm">
                                        (₹{totalPrice} total: ₹{basePrice} base + ₹{itemPrices} items)
                                      </span>
                                    );
                                  })()}
                                </h4>
                                <div className="flex flex-wrap gap-2 mb-4">
                                  {categorySelections.map(selection => (
                                    <Badge key={selection.menuItemId} variant="secondary" className="bg-yellow-600 text-white">
                                      {selection.menuItemName}
                                    </Badge>
                                  ))}
                                </div>
                                
                                {/* Next Category Navigation */}
                                {isCategoryComplete && nextCategory && (
                                  <div className="border-t border-gray-600 pt-4">
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                      <div className="text-sm text-gray-300">
                                        Great! {category.displayName} selection complete.
                                      </div>
                                      <Button 
                                        onClick={() => {
                                          setActiveTab(nextCategory.name);
                                          // Scroll to top of new category section with smooth animation
                                          setTimeout(() => {
                                            const element = document.querySelector(`[data-state="active"][data-value="${nextCategory.name}"]`);
                                            if (element) {
                                              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                            } else {
                                              // Fallback: scroll to tabs container
                                              const tabsContainer = document.querySelector('[role="tablist"]');
                                              if (tabsContainer) {
                                                tabsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                              }
                                            }
                                          }, 100);
                                        }}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center justify-center space-x-2 w-full sm:w-auto"
                                      >
                                        <span>Next: {nextCategory.displayName}</span>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                      </Button>
                                    </div>
                                  </div>
                                )}
                                
                                {/* All Categories Complete Message */}
                                {isCategoryComplete && !nextCategory && (
                                  <div className="border-t border-gray-600 pt-4">
                                    <div className="text-center">
                                      <div className="text-green-400 font-semibold mb-2">
                                        🎉 All menu categories completed!
                                      </div>
                                      <div className="text-sm text-gray-300">
                                        Scroll down to continue with your order details.
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </TabsContent>
                    ));
                  })()}
                </Tabs>

                {/* Navigation - Inside Menu Selection Area */}
                <div className="mt-8 pt-6 border-t border-gray-600">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setCurrentStep('package')}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 w-full sm:w-auto"
                    >
                      Back to Packages
                    </Button>
                    <div className="text-center w-full sm:w-auto">
                      <div className="text-sm text-gray-400 mb-2">
                        Complete all menu categories to continue
                      </div>
                      <Button 
                        onClick={() => setCurrentStep('details')}
                        disabled={!areMinimumRequirementsMet()}
                        className={`font-semibold px-8 w-full sm:w-auto ${
                          areMinimumRequirementsMet() 
                            ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
                            : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Continue to Details
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Order Details */}
      {currentStep === 'details' && (
        <div className="space-y-6 max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center">Order Details</h2>
          
          <Card className="bg-gray-800 border-gray-600">
            <CardContent className="p-6 space-y-4">
              <div>
                <Label htmlFor="wedding-select" className="text-white">Select Event</Label>
                <Select value={selectedWedding?.toString()} onValueChange={(value) => setSelectedWedding(parseInt(value))}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Choose your event" />
                  </SelectTrigger>
                  <SelectContent>
                    {weddings.map((wedding: any) => (
                      <SelectItem key={wedding.id} value={wedding.id.toString()}>
                        {wedding.title} - {wedding.weddingDate ? formatDateLongDisplay(wedding.weddingDate) : 'Date TBD'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="delivery-date" className="text-white">Delivery Date</Label>
                  <Input
                    id="delivery-date"
                    type="date"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    className="mt-2"
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <p className="text-gray-400 text-sm mt-1">Default: Event day</p>
                </div>
                
                <div>
                  <Label htmlFor="delivery-time" className="text-white">Delivery Time</Label>
                  <Select value={deliveryTime} onValueChange={setDeliveryTime}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">Day Event (10:00 AM - 4:00 PM)</SelectItem>
                      <SelectItem value="evening">Evening Event (5:00 PM - 11:00 PM)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-gray-400 text-sm mt-1">Auto-detected from event details</p>
                </div>
              </div>

              <div>
                <Label htmlFor="special-requests" className="text-white">Special Requests</Label>
                <Textarea
                  id="special-requests"
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  placeholder="Any special dietary requirements, allergies, or specific requests..."
                  className="mt-2"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => setCurrentStep('menu')}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Back to Menu
            </Button>
            <Button 
              onClick={() => setCurrentStep('review')}
              disabled={!selectedWedding}
              className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold"
            >
              Review Order
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Order Review */}
      {currentStep === 'review' && selectedPackage && (
        <div className="space-y-6 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center">Review Your Order</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Order Summary - Narrower */}
            <Card className="bg-gray-800 border-gray-600 lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-white">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold text-white">Package: {selectedPackage.name}</h4>
                  <p className="text-gray-300 text-sm">{selectedPackage.description}</p>
                </div>
                
                <div>
                  <span className="text-gray-300 text-sm mb-2 block">Guest Count:</span>
                  <div className="flex items-center justify-between bg-gray-700 rounded-lg p-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setGuestCount(Math.max(50, guestCount - 10))}
                      className="h-8 w-8 p-0 border-gray-600 text-gray-300 hover:bg-gray-600 hover:text-white"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="text-white font-bold text-lg mx-4">{guestCount}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setGuestCount(guestCount + 10)}
                      className="h-8 w-8 p-0 border-gray-600 text-gray-300 hover:bg-gray-600 hover:text-white"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Minimum 50 guests, increments of 10</p>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-300">Per Head Rate:</span>
                  <span className="text-white">₹{Math.round(calculateTotalPrice() / guestCount)}</span>
                </div>
                
                <div className="flex justify-between text-lg font-bold border-t border-gray-600 pt-2">
                  <span className="text-white">Total Price:</span>
                  <span className="text-yellow-500">₹{calculateTotalPrice()}</span>
                </div>
              </CardContent>
            </Card>

            {/* Selected Items - Wider */}
            <Card className="bg-gray-800 border-gray-600 lg:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-lg">Selected Menu Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(
                    selections.reduce((acc, selection) => {
                      if (!acc[selection.categoryName]) {
                        acc[selection.categoryName] = [];
                      }
                      acc[selection.categoryName].push(selection);
                      return acc;
                    }, {} as Record<string, OrderSelection[]>)
                  ).map(([categoryName, items]) => {
                    // Calculate category pricing information
                    const getCategoryPricing = () => {
                      const hasBasePriceItems = items.some(item => parseFloat(item.price) === 0);
                      let basePriceInfo = '';
                      let individualItemsTotal = 0;
                      
                      // Calculate individual items total for premium/additional items
                      items.forEach(item => {
                        if (parseFloat(item.price) > 0) {
                          individualItemsTotal += parseFloat(item.price);
                        }
                      });
                      
                      // Get base price for categories with base pricing
                      if (hasBasePriceItems) {
                        if (categoryName === 'juices') {
                          basePriceInfo = '₹25';
                        } else if (categoryName === 'starters') {
                          basePriceInfo = '₹40';
                        } else if (categoryName === 'breads') {
                          basePriceInfo = '₹20';
                        } else if (categoryName === 'veg_counter') {
                          basePriceInfo = '₹30';
                        } else if (categoryName === 'desserts') {
                          basePriceInfo = '₹25';
                        } else if (categoryName === 'settings_staff') {
                          basePriceInfo = '₹50';
                        }
                      }
                      
                      // Combine base price and individual items
                      if (basePriceInfo && individualItemsTotal > 0) {
                        return basePriceInfo + ' + ₹' + individualItemsTotal + ' items';
                      } else if (basePriceInfo) {
                        return basePriceInfo;
                      } else if (individualItemsTotal > 0) {
                        return '₹' + individualItemsTotal + ' total';
                      } else {
                        return 'Individual pricing';
                      }
                    };
                    
                    return (
                      <div key={categoryName} className="bg-gray-700 p-2 rounded-lg">
                        <h5 className="font-semibold text-yellow-500 capitalize mb-2 flex items-center text-sm">
                          {getCategoryIcon(categoryName)}
                          <span className="ml-2">{categoryName.replace('_', ' ')}</span>
                          <span className="ml-auto text-xs text-gray-300">
                            ({items.length} items - {getCategoryPricing()})
                          </span>
                        </h5>
                        <div className="space-y-1">
                          {items.map((item, index) => (
                            <div key={index} className="flex justify-between items-center text-xs bg-gray-600 p-1.5 rounded">
                              <span className="text-gray-200 flex-1 mr-2 truncate">{item.menuItemName}</span>
                              <span className="text-yellow-400 font-medium text-xs">
                                {parseFloat(item.price) === 0 ? 'Included' : '₹' + item.price}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Order Details Summary */}
          <Card className="bg-gray-800 border-gray-600">
            <CardHeader>
              <CardTitle className="text-white">Delivery Details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-300">Event:</span>
                <p className="text-white font-semibold">
                  {weddings.find((w: any) => w.id === selectedWedding)?.title || 'Unknown Event'}
                </p>
              </div>
              <div>
                <span className="text-gray-300">Delivery Date:</span>
                <p className="text-white font-semibold">{deliveryDate || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-gray-300">Delivery Time:</span>
                <p className="text-white font-semibold">{deliveryTime || 'Not specified'}</p>
              </div>
              {specialRequests && (
                <div className="md:col-span-3">
                  <span className="text-gray-300">Special Requests:</span>
                  <p className="text-white">{specialRequests}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <Button 
              variant="outline" 
              onClick={() => setCurrentStep('details')}
              className="border-gray-600 text-gray-300 hover:bg-gray-700 w-full sm:w-auto"
            >
              Back to Details
            </Button>
            <Button 
              onClick={() => {
                setCurrentStep('payment');
                // Scroll to top of payment section instead of bottom
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 100);
              }}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 w-full sm:w-auto"
            >
              Proceed to Payment
            </Button>
          </div>
        </div>
      )}

      {/* Step 5: Payment */}
      {currentStep === 'payment' && selectedPackage && (
        <div className="space-y-6 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center">Payment Details</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Payment Breakdown */}
            <Card className="bg-gray-800 border-gray-600">
              <CardHeader>
                <CardTitle className="text-white">Payment Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(() => {
                  const pricing = calculateFinalPricing();
                  
                  return (
                    <>
                      <div className="flex justify-between text-gray-300">
                        <span>Subtotal ({guestCount} guests):</span>
                        <span>₹{pricing.subtotal.toLocaleString()}</span>
                      </div>
                      
                      <div className="flex justify-between text-blue-400 text-sm">
                        <span>Per Head Rate (before discount/GST):</span>
                        <span>₹{Math.round(pricing.subtotal / guestCount)}</span>
                      </div>
                      
                      {pricing.discountRate > 0 && (
                        <div className="flex justify-between text-green-400">
                          <span>Discount ({(pricing.discountRate * 100)}%):</span>
                          <span>-₹{pricing.discount.toLocaleString()}</span>
                        </div>
                      )}
                      
                      {pricing.setupFee > 0 && (
                        <div className="flex justify-between text-orange-400">
                          <span>Setup Fee (Under 400 guests):</span>
                          <span>₹{pricing.setupFee.toLocaleString()}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between text-gray-300">
                        <span>GST (5%):</span>
                        <span>₹{pricing.gstAmount.toLocaleString()}</span>
                      </div>
                      
                      <div className="border-t border-gray-600 pt-3">
                        <div className="flex justify-between text-white font-bold text-lg">
                          <span>Total Amount:</span>
                          <span>₹{pricing.totalAmount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-yellow-500 text-sm mt-1">
                          <span>Final Per Head Rate (with discount & GST):</span>
                          <span>₹{Math.round(pricing.totalAmount / guestCount)}</span>
                        </div>
                      </div>
                      
                      <div className="bg-green-900/30 p-4 rounded-lg border border-green-600">
                        <h4 className="text-green-400 font-semibold mb-2">Payment Schedule</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-300">Advance Payment (20%):</span>
                            <span className="text-white font-semibold">₹{pricing.advanceAmount.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Remaining (Due 10 days before event):</span>
                            <span className="text-white">₹{pricing.remainingAmount.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Discount & Fee Information */}
            <Card className="bg-gray-800 border-gray-600">
              <CardHeader>
                <CardTitle className="text-white">Pricing Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-yellow-500 font-semibold mb-3">Discount Rates (Guest Count Based)</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">400 to 500 Guests:</span>
                      <span className="text-green-400">3% Discount</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">500 to 600 Guests:</span>
                      <span className="text-green-400">5% Discount</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">600 to 800 Guests:</span>
                      <span className="text-green-400">7% Discount</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">800 to 1000 Guests:</span>
                      <span className="text-green-400">8.5% Discount</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">1000+ Guests:</span>
                      <span className="text-green-400">10% Discount</span>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-900/30 p-4 rounded-lg border border-orange-600">
                  <h4 className="text-orange-400 font-semibold mb-2">Setup Fee (Under 400 Guests)</h4>
                  <p className="text-orange-200 text-sm mb-2">
                    Additional setup fee applies for events with less than 400 guests based on menu selection.
                  </p>
                  <p className="text-orange-300 text-xs">
                    Maximum setup fee: ₹10,000. Actual rate will be communicated upon order confirmation.
                  </p>
                </div>

                <div className="bg-blue-900/30 p-4 rounded-lg border border-blue-600">
                  <h4 className="text-blue-400 font-semibold mb-2">Important Notes</h4>
                  <ul className="text-blue-200 text-sm space-y-1">
                    <li>• Discount rates shown are indicative only</li>
                    <li>• Actual discount rates may vary based on menu selection</li>
                    <li>• Final discounted rate will be communicated upon order confirmation</li>
                    <li>• 5% GST applies to all orders</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <Card className="bg-gray-800 border-gray-600">
            <CardHeader>
              <CardTitle className="text-white">Order Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-300">Package:</span>
                  <p className="text-white font-semibold">{selectedPackage.name}</p>
                </div>
                <div>
                  <span className="text-gray-300">Guest Count:</span>
                  <p className="text-white font-semibold">{guestCount}</p>
                </div>
                <div>
                  <span className="text-gray-300">Total Items:</span>
                  <p className="text-white font-semibold">{selections.length} selected</p>
                </div>
                <div>
                  <span className="text-gray-300">Event:</span>
                  <p className="text-white font-semibold">
                    {weddings.find((w: any) => w.id === selectedWedding)?.title || 'Unknown Event'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-300">Delivery Date:</span>
                  <p className="text-white font-semibold">{deliveryDate || 'Not specified'}</p>
                </div>
                <div>
                  <span className="text-gray-300">Delivery Time:</span>
                  <p className="text-white font-semibold">{deliveryTime || 'Not specified'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <Button 
              variant="outline" 
              onClick={() => setCurrentStep('review')}
              className="border-gray-600 text-gray-300 hover:bg-gray-700 w-full sm:w-auto"
            >
              Back to Review
            </Button>
            <Button
              onClick={handleSubmitOrder}
              disabled={!razorpayReady || createFinalOrderMutation.isPending || createOrderMutation.isPending}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 w-full sm:w-auto"
            >
              {!razorpayReady
                ? 'Loading payment…'
                : (createFinalOrderMutation.isPending || createOrderMutation.isPending)
                  ? 'Processing Payment...'
                  : 'Pay Advance Amount'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}